import { Link, useLocation } from 'react-router-dom';

const RUSIV_URL = 'https://rusiv.com';

const navigationLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Vision', path: '/vision' },
  { label: 'Rusiv', path: '/rusiv' },
];

const companyLinks = [
  { label: 'Privacy Policy', path: '/privacy' },
  { label: 'Terms of Service', path: '/terms' },
  { label: 'Careers', path: '/careers' },
  { label: 'Contact', path: '/contact' },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();

  const handleFooterNavClick = (path) => {
    if (location.pathname === path) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <footer
      className="footer-custom"
      aria-labelledby="footer-brand-title"
      style={{ padding: 'clamp(3.5rem, 7vw, 5rem) 0 2rem' }}
    >
      <div className="container">
        <div className="row g-4 g-lg-5">
          <div className="col-lg-5 col-md-12">
            <div style={{ maxWidth: '430px' }}>
              <Link
                to="/"
                onClick={() => handleFooterNavClick('/')}
                aria-label="Go to Allyverto home page"
                style={{ textDecoration: 'none' }}
              >
                <h2
                  id="footer-brand-title"
                  className="gradient-text mb-3"
                  style={{
                    fontFamily: 'var(--font-serif)',
                    fontSize: 'clamp(2rem, 4vw, 2.8rem)',
                    fontWeight: 400,
                    letterSpacing: '-0.035em',
                  }}
                >
                  Allyverto
                </h2>
              </Link>

              <p
                className="mb-4"
                style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.96rem',
                  lineHeight: 1.75,
                }}
              >
                Allyverto builds personal intelligence designed to understand the
                individual, adapt with context, and become more useful over time.
              </p>

              <p
                className="mb-4"
                style={{
                  color: 'var(--text-muted)',
                  fontSize: '0.82rem',
                  lineHeight: 1.65,
                  maxWidth: '390px',
                }}
              >
                Our first product, Rusiv, brings that direction into a conversational
                experience built around continuity, clarity, and the person using it.
              </p>

              <div
                className="d-flex align-items-center gap-2"
                aria-label="Allyverto social links"
              >
                <a
                  href="https://linkedin.com/company/allyverto"
                  className="btn btn-outline-light btn-sm rounded-circle"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit Allyverto on LinkedIn"
                  title="LinkedIn"
                >
                  <i className="fab fa-linkedin-in" aria-hidden="true" />
                </a>

                {/*
                  GitHub is intentionally hidden for now.
                  Uncomment this block when the public GitHub profile is ready.

                  <a
                    href="https://github.com/allyverto"
                    className="btn btn-outline-light btn-sm rounded-circle"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit Allyverto on GitHub"
                    title="GitHub"
                  >
                    <i className="fab fa-github" aria-hidden="true" />
                  </a>
                */}

                <a
                  href="https://instagram.com/allyverto"
                  className="btn btn-outline-light btn-sm rounded-circle"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit Allyverto on Instagram"
                  title="Instagram"
                >
                  <i className="fab fa-instagram" aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>

          <div className="col-lg-2 col-md-4 col-6">
            <h3
              className="mb-4"
              style={{
                color: 'var(--accent)',
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              Explore
            </h3>

            <ul className="list-unstyled mb-0">
              {navigationLinks.map((item) => (
                <li className="mb-3" key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => handleFooterNavClick(item.path)}
                    style={{
                      color: 'var(--text-secondary)',
                      textDecoration: 'none',
                      fontSize: '0.92rem',
                    }}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-lg-2 col-md-4 col-6">
            <h3
              className="mb-4"
              style={{
                color: 'var(--accent)',
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              Company
            </h3>

            <ul className="list-unstyled mb-0">
              {companyLinks.map((item) => (
                <li className="mb-3" key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => handleFooterNavClick(item.path)}
                    style={{
                      color: 'var(--text-secondary)',
                      textDecoration: 'none',
                      fontSize: '0.92rem',
                    }}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-lg-3 col-md-4">
            <h3
              className="mb-4"
              style={{
                color: 'var(--accent)',
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              Our first product
            </h3>

            <div
              style={{
                padding: '1.25rem',
                border: '1px solid var(--border-color)',
                borderRadius: '16px',
                background: 'var(--bg-secondary)',
              }}
            >
              <span
                style={{
                  display: 'block',
                  marginBottom: '0.45rem',
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-serif)',
                  fontSize: '1.35rem',
                }}
              >
                Rusiv
              </span>

              <p
                className="mb-3"
                style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.86rem',
                  lineHeight: 1.65,
                }}
              >
                A personal AI experience designed to understand context, continue
                meaningfully, and adapt to the person using it.
              </p>

              <a
                href={RUSIV_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary-custom"
                style={{
                  width: '100%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.55rem',
                  background: 'var(--accent)',
                  color: 'var(--bg-primary)',
                }}
              >
                Visit Rusiv
                <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>

        <hr
          className="my-4"
          style={{
            borderColor: 'var(--border-color)',
            opacity: 0.45,
          }}
        />

        <div className="row align-items-center g-3">
          <div className="col-md-7">
            <p
              className="mb-0"
              style={{
                color: 'var(--text-secondary)',
                fontSize: '0.82rem',
              }}
            >
              © {currentYear} Allyverto IT Technologies Pvt. Ltd. All rights reserved.
            </p>
          </div>

          <div className="col-md-5 text-md-end">
            <p
              className="mb-0"
              style={{
                color: 'var(--text-muted)',
                fontSize: '0.82rem',
              }}
            >
              Building intelligence around the person.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;